import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, Edit, Trash2, MoreHorizontal, FileText, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogList = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(res.data);
    } catch (error) { toast.error('Failed to fetch'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    try {
      console.log('Attempting to delete blog post:', deleteId);
      const response = await axios.delete(`${API}/admin/blog/${deleteId}`, { withCredentials: true });
      console.log('Delete response:', response.data);
      toast.success('Deleted');
      fetchPosts();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setDeleteId(null);
    }
  };

  const filtered = posts.filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const variants = { draft: 'secondary', published: 'default', archived: 'outline' };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Blog Posts</h1><p className="text-gray-500">Manage your blog content</p></div>
        <Button onClick={() => navigate('/admin/blog/new')} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> New Post</Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead>Views</TableHead><TableHead>Date</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow> :
            filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8"><FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" /><p className="text-gray-500">No posts</p></TableCell></TableRow> :
            filtered.map(post => (
              <TableRow key={post.post_id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {post.featured_image_url ? <img src={post.featured_image_url.startsWith('/') ? `${BACKEND_URL}${post.featured_image_url}` : post.featured_image_url} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center"><FileText className="w-5 h-5 text-gray-400" /></div>}
                    <div><p className="font-medium max-w-md truncate">{post.title}</p>{post.excerpt && <p className="text-sm text-gray-500 truncate max-w-md">{post.excerpt}</p>}</div>
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline">{post.category}</Badge></TableCell>
                <TableCell>{getStatusBadge(post.status)}</TableCell>
                <TableCell><div className="flex items-center gap-1"><Eye className="w-4 h-4 text-gray-400" />{post.views_count || 0}</div></TableCell>
                <TableCell>{post.published_at ? new Date(post.published_at).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/admin/blog/${post.post_id}`)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteId(post.post_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Post</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

export default BlogList;
